import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Check,
  X,
  Trash2,
  Star,
  RefreshCw,
  Pin,
} from 'lucide-react';
import API_BASE from '../services/apiBase';
import { resolvePreviewUrl } from '../utils/images';
import { useToast } from '../features/toast/ToastContext';
import { StarDisplay } from './StarRating';

function formatDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '—';
  return x.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function statusBadge(status) {
  if (status === 'approved') return 'jet-badge jet-badge-success';
  if (status === 'rejected') return 'jet-badge jet-badge-warn';
  return 'jet-badge jet-badge-neutral';
}

export default function AdminReviewsPanel({ apiBase, authToken, isActive }) {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const headers = useMemo(
    () => ({
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    }),
    [authToken],
  );

  const load = useCallback(async () => {
    if (!authToken || !isActive) return;
    setLoading(true);
    setError('');
    try {
      const q = filter !== 'all' ? `?status=${filter}` : '';
      const res = await fetch(`${apiBase}/api/reviews${q}`, { headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Failed to load reviews');
      setRows(Array.isArray(json.data) ? json.data : []);
    } catch (e) {
      setError(e.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [apiBase, authToken, filter, headers, isActive]);

  useEffect(() => {
    load();
  }, [load]);

  const patchReview = async (id, body) => {
    setBusyId(id);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/reviews/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Update failed');
      setRows((prev) => prev.map((r) => (r._id === id ? json.data : r)));
      toast.success('Review updated');
    } catch (e) {
      setError(e.message || 'Update failed');
      toast.error(e.message || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    setBusyId(id);
    setError('');
    try {
      const res = await fetch(`${apiBase}/api/reviews/${id}`, { method: 'DELETE', headers });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Delete failed');
      setRows((prev) => prev.filter((r) => r._id !== id));
      toast.success('Review deleted');
    } catch (e) {
      setError(e.message || 'Delete failed');
      toast.error(e.message || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const pendingCount = rows.filter((r) => r.status === 'pending').length;

  return (
    <div className="jet-card">
      <div className="jet-table-toolbar">
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="jet-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All reviews</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          {pendingCount > 0 && filter === 'all' && (
            <span className="jet-badge jet-badge-warn">{pendingCount} pending</span>
          )}
        </div>
        <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" onClick={load} disabled={loading} title="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {error ? <p className="jet-finance-empty" style={{ color: 'var(--jet-danger, #dc2626)' }}>{error}</p> : null}

      {loading && !rows.length ? (
        <p className="jet-finance-empty">Loading reviews…</p>
      ) : !rows.length ? (
        <p className="jet-finance-empty">No reviews yet. Customers can submit from /review on the website.</p>
      ) : (
        <div className="jet-table-wrap">
          <table className="jet-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th>HBL</th>
                <th>Rating</th>
                <th>Comment</th>
                <th>Photo</th>
                <th>Status</th>
                <th>Home</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const photoSrc = row.photoUrl
                  ? resolvePreviewUrl(row.photoUrl, API_BASE, row._id)
                  : '';
                return (
                  <tr key={row._id}>
                    <td>{formatDate(row.createdAt)}</td>
                    <td>
                      <div>{row.name}</div>
                      {row.email ? <div style={{ fontSize: '0.8rem', color: 'var(--jet-muted)' }}>{row.email}</div> : null}
                    </td>
                    <td>{row.hblNo || '—'}</td>
                    <td><StarDisplay rating={row.rating} size={14} /></td>
                    <td style={{ maxWidth: 220 }}>{row.comment}</td>
                    <td>
                      {photoSrc ? (
                        <a href={photoSrc} target="_blank" rel="noreferrer" title="Open photo">
                          <img
                            src={photoSrc}
                            alt=""
                            style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--jet-border)' }}
                          />
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td><span className={statusBadge(row.status)}>{row.status}</span></td>
                    <td>
                      {row.status === 'approved' && row.rating >= 5 ? (
                        <button
                          type="button"
                          className={`jet-btn jet-btn-icon ${row.isFeatured ? 'jet-btn-secondary' : ''}`}
                          title={row.isFeatured ? 'Featured on homepage' : 'Feature on homepage'}
                          disabled={busyId === row._id}
                          onClick={() => patchReview(row._id, { isFeatured: !row.isFeatured })}
                        >
                          <Pin className="w-4 h-4" />
                        </button>
                      ) : (
                        <span style={{ color: 'var(--jet-muted)', fontSize: '0.85rem' }}>—</span>
                      )}
                    </td>
                    <td>
                      <div className="jet-finance-row-actions">
                        {row.status !== 'approved' && (
                          <button
                            type="button"
                            className="jet-btn jet-btn-secondary jet-btn-icon"
                            title="Approve"
                            disabled={busyId === row._id}
                            onClick={() => patchReview(row._id, { status: 'approved' })}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        {row.status !== 'rejected' && (
                          <button
                            type="button"
                            className="jet-btn jet-btn-icon"
                            title="Reject"
                            disabled={busyId === row._id}
                            onClick={() => patchReview(row._id, { status: 'rejected' })}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          className="jet-btn jet-btn-danger jet-btn-icon"
                          title="Delete"
                          disabled={busyId === row._id}
                          onClick={() => deleteReview(row._id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'var(--jet-muted)' }}>
        <Star className="w-3.5 h-3.5" style={{ display: 'inline', verticalAlign: '-2px' }} />
        {' '}
        Only approved 5-star reviews appear on the public homepage.
      </p>
    </div>
  );
}
